import React from 'react';
import * as yup from 'yup';
import { FormikHelpers, useFormikContext } from 'formik';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import { useHistory } from 'react-router-dom';
import { reqEmail, reqString } from '../../data/validations';
import XForm from '../../components/forms/XForm';
import XTextInput from '../../components/inputs/XTextInput';
import { remoteRoutes, localRoutes } from '../../data/constants';
import { post } from '../../utils/ajax';
import Toast from '../../utils/Toast';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import { useLoginStyles } from './loginStyles';

export interface IGoogleSignup {
  credential: string;
  email: string;
  firstName: string;
  lastName: string;
}

interface IProps {
  data: any | null;
  done?: () => any;
  /** Set when they came in through Google — no password, email is fixed. */
  google?: IGoogleSignup | null;
}

// Google has already proved the address and there is no password to pick,
// so those checks only apply to the ordinary form. Hub, course, gender,
// DOB and phone are no longer collected here — they're asked later, at
// first course enrollment.
const buildSchema = (google?: IGoogleSignup | null) =>
  yup.object().shape({
    firstName: reqString,
    lastName: reqString,
    email: reqEmail,
    password: google
      ? yup.string()
      : yup
          .string()
          .min(8, 'Password must be at least 8 characters')
          .required('Password is required'),
    confirmPassword: google
      ? yup.string()
      : yup
          .string()
          .oneOf([yup.ref('password')], 'Passwords must match')
          .required('Please confirm your password'),
  });

const initialValues = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  confirmPassword: '',
};

// Renders inside XForm's <Form>, so it shares the same Formik context.
const SubmitButton = () => {
  const classes = useLoginStyles();
  const { submitForm, isSubmitting } = useFormikContext();

  return (
    <Button
      fullWidth
      variant="contained"
      color="primary"
      disableElevation
      disabled={isSubmitting}
      onClick={submitForm}
      className={classes.submit}
    >
      {isSubmitting ? 'Creating Account…' : 'Create Account'}
    </Button>
  );
};

const RegisterForm = ({ done, google }: IProps) => {
  const history = useHistory();
  const startValues = google
    ? {
        ...initialValues,
        email: google.email,
        firstName: google.firstName || '',
        lastName: google.lastName || '',
      }
    : initialValues;

  function handleSubmit(values: any, actions: FormikHelpers<any>) {
    const toSave: any = {
      // Unrelated to a physical hub — this satisfies the app's single-tenant
      // multi-tenancy header check, same as Login.tsx.
      hubName: 'elevate',
      firstName: values.firstName,
      lastName: values.lastName,
      email: google ? google.email : values.email,
      // The server re-verifies this token and takes the address from it, so
      // a Google signup needs no password of its own.
      ...(google
        ? { googleCredential: google.credential }
        : { password: values.password }),
    };

    post(
      remoteRoutes.register,
      toSave,
      () => {
        actions.resetForm();
        if (google) {
          // Google already proved this address — nothing to verify.
          Toast.info(
            'Thank you for joining era92 elevate! Your account is ready — you can now sign in with your email and password.',
          );
          if (done) done();
          return;
        }
        Toast.info("We've sent a code to confirm your email address.");
        // HashRouter can't carry router state (it's silently dropped), so
        // the email rides along as a query param instead.
        history.push(
          `${localRoutes.verifyEmail}?email=${encodeURIComponent(
            toSave.email,
          )}`,
        );
      },
      undefined,
      () => {
        actions.setSubmitting(false);
      },
    );
  }

  return (
    <XForm
      onSubmit={handleSubmit}
      schema={buildSchema(google)}
      initialValues={startValues}
      hideSubmit
    >
      {/* Grid gutters are 8px each side; this offsets them so the fields line
          up with the full-width buttons below. */}
      <div style={{ padding: 8, margin: -8 }}>
        <Grid spacing={2} container className="min-width-100">
          <Grid item xs={12} sm={6}>
            <XTextInput
              name="firstName"
              label="First name"
              type="text"
              variant="outlined"
              margin="none"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <XTextInput
              name="lastName"
              label="Last name"
              type="text"
              variant="outlined"
              margin="none"
            />
          </Grid>

          <Grid item xs={12}>
            <XTextInput
              name="email"
              label="Email address"
              type="email"
              variant="outlined"
              margin="none"
              startIcon={<MailOutlineIcon fontSize="small" />}
              disabled={!!google}
              helperText={google ? 'Confirmed by Google' : undefined}
            />
          </Grid>

          {!google && (
            <Grid item xs={12}>
              <XTextInput
                name="password"
                label="Password"
                variant="outlined"
                margin="none"
                isPassword
                startIcon={<LockOutlinedIcon fontSize="small" />}
              />
            </Grid>
          )}

          {!google && (
            <Grid item xs={12}>
              <XTextInput
                name="confirmPassword"
                label="Confirm password"
                variant="outlined"
                margin="none"
                isPassword
                startIcon={<LockOutlinedIcon fontSize="small" />}
              />
            </Grid>
          )}
        </Grid>

        <SubmitButton />
      </div>
    </XForm>
  );
};

export default RegisterForm;
